/**
 * Shared presentation for the Users screen — the badges and formatting the table, the
 * detail dialog and the rule card all render the same way.
 *
 * Kept in one place because a provider or role-source badge that means one thing on the
 * list and another in the detail view is worse than no badge at all.
 */

import { Badge } from "@/components/ui/badge";
import type { AuthProvider, RoleSource } from "@/hooks/use-users";

const PROVIDER_LABELS: Record<AuthProvider, string> = {
  microsoft: "Microsoft",
  google: "Google",
  credential: "Password",
};

/**
 * How someone signs in. Microsoft is the one that matters on this screen — it is the
 * SSO path new people arrive through, and the one automatic role mapping acts on — so it
 * gets the emphasised treatment and the others stay quiet.
 */
export function ProviderBadge({ provider }: { provider: AuthProvider }) {
  if (provider === "microsoft") {
    return (
      <Badge className="bg-info-100 text-info-700 hover:bg-info-100">
        {PROVIDER_LABELS.microsoft}
      </Badge>
    );
  }
  return <Badge variant="outline">{PROVIDER_LABELS[provider]}</Badge>;
}

const ROLE_SOURCE_LABELS: Record<RoleSource, string> = {
  manual: "Set by hand",
  auto: "Automatic",
  default: "Default",
};

const ROLE_SOURCE_TITLES: Record<RoleSource, string> = {
  manual: "An admin chose this role. Automatic mapping will not change it.",
  auto: "A role-mapping rule set this role, and will keep it up to date on sign-in.",
  default: "Nothing has decided this role yet — it is the default for a new account.",
};

export function RoleSourceBadge({ source }: { source: RoleSource }) {
  return (
    <Badge variant="outline" title={ROLE_SOURCE_TITLES[source]}>
      {ROLE_SOURCE_LABELS[source]}
    </Badge>
  );
}

/** A last-sign-in stamp, or a plain "Never" rather than an empty cell. */
export function formatSignIn(value: string | null): string {
  if (!value) return "Never";
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}
